/* eslint-disable react/no-array-index-key  */
import {css, useTheme} from '@mui/material';
import {useApiQuery, LoadingSpinner, useLocalStorage, DlMuiButton, DlMuiDynamicIcon, GetStylesFunction} from '@wallaby-ui/core';
import {LightScrollbarWrapper} from '@wallaby-ui/document';
import {components} from '@wallaby-ui/document/types/js/common/_generated/haidi_schema';
import React, {useCallback, useMemo} from 'react';

import EncounterHaiItem from './EncounterHaiItem';
import {AppConfig} from 'src/js/app_config';

type Props = {
  encounterId: string;
};

function useHaiData(encounterId: string) {
  const url = AppConfig.service.url.hais.replace('{encounterId}', encounterId);
  const {
    data,
    isLoading,
    isError: _,
    isRefetching,
    refetch,
  } = useApiQuery<components['schemas']['HAIDetailDto'][]>([url], null, {
    axiosConfig: {
      url,
    },
  });

  return {data, isLoading: isLoading || isRefetching, refetch};
}

export const EncounterHaiList: React.FC<Props> = ({encounterId}) => {
  const {data, isLoading, refetch} = useHaiData(encounterId);

  const [defaultScroll, setDefaultScroll] = useLocalStorage(`/encounter/${encounterId}.EncounterHaiList`, 0);
  const storageIdPrefix = `/encounter/${encounterId}`;
  const [addingNewHai, _, setAddingNewHai] = useLocalStorage(`${storageIdPrefix}.addingNewHai`, false);
  const cancelAddingHai = useCallback(() => {
    setAddingNewHai(false);
  }, [setAddingNewHai]);
  const confirmedHaiTypes = useMemo(() => {
    return (data ?? []).filter((v) => v.state === 'YES').map((v) => v.type);
  }, [data]);

  const theme = useTheme();
  const muiStyles = getStyles({theme});
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        background: 'white',
        padding: '16px 1px 16px 16px',
      }}
    >
      <LightScrollbarWrapper
        style={{paddingRight: 1}}
        defaultScroll={{to: defaultScroll, dependOn: null}}
        onScroll={(event) => {
          setDefaultScroll((event.target as HTMLDivElement).scrollTop);
        }}
      >
        <LoadingSpinner isVisible={isLoading} />

        {!isLoading && (
          <div
            data-name='encounter-hai-list'
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            <DlMuiButton
              data-name='add-hai-button'
              data-adding={addingNewHai}
              type='chip2-filled'
              big
              title='hai.miniScreen.hai.add.button'
              onClick={() => {
                setAddingNewHai(true);
              }}
              disabled={addingNewHai}
              style={{width: '100%'}}
              leftIcon={
                <DlMuiDynamicIcon
                  iconName='haidiAddIcon'
                  useFixedViewBox={16}
                  style={{
                    color: 'inherit',
                    padding: 0,
                    margin: 0,
                    pointerEvents: 'none',
                  }}
                  svgIconInputCss={muiStyles.addIcon}
                  noButtonStyles
                />
              }
            />
            {addingNewHai && (
              <EncounterHaiItem
                key={0}
                encounterId={encounterId}
                hai={undefined}
                config={AppConfig}
                refetchHais={refetch}
                isNewHai
                cancelAddingHai={cancelAddingHai}
                confirmedHaiTypes={confirmedHaiTypes}
              />
            )}
            {data?.map((v) => (
              <EncounterHaiItem
                key={v.id}
                encounterId={encounterId}
                hai={v}
                config={AppConfig}
                refetchHais={refetch}
                cancelAddingHai={cancelAddingHai}
                confirmedHaiTypes={confirmedHaiTypes}
              />
            ))}
          </div>
        )}
      </LightScrollbarWrapper>
    </div>
  );
};
const getStyles: GetStylesFunction = (options) => {
  return {
    addIcon: css`
      font-size: 16px;
    `,
  };
};
